/**
 * Whether THIS fan has already been established as enrolled, for the rest of the tab session.
 *
 * Only a positive verdict is stored. Re-reading a yes on every page of a browse is pure waste, while
 * a no has to stay re-askable because the fan may join on the very next click. See D-050.
 *
 * **"Enrolment is a one-way door -- there is no un-join" was the other half of that sentence until
 * 2026-09-22, and it is now false.** The Loyalty Profile block can take a fan out of the programme,
 * so a stored yes can go stale within the tab that stored it -- and `sessionStorage` survives the
 * redirect that flow ends on. `forgetEnrolled()` below is what closes that, and the leave funnel
 * calls it.
 *
 * The exposure is narrower than it looks, and worth knowing exactly, because it decides whether this
 * is hygiene or a fix. `toasts/gate.js` settles `no-consent` at gate 2.5 and never reaches
 * `askEnrolment()`, so a fan whose consent reads a definite `false` is answered correctly whatever
 * this cache holds. What it does NOT cover is gate 2.5's fail-open twin: an unreadable consent
 * answer -- a Management API outage, a misconfigured install -- settles `consent-unknown`, which
 * carries on to the enrolment question, which this cache answers `armed` for a fan who has left.
 * That one verdict then repoints the header's loyalty links and stops `../nonMemberRedirect.js`
 * bouncing them off a members-only page. So: a fix, for a window nobody would ever reproduce by
 * accident.
 *
 * **The fan is part of what makes a HIT, and that is the whole point of this module existing.** The
 * verdict used to be a bare `'1'` under an unscoped key, which meant a tab that established
 * "enrolled" for one fan kept answering `armed` for whoever signed in after them -- and because
 * `gate.js` is memoised for three callers, that one stale yes also repointed the header's loyalty
 * links and stopped `../nonMemberRedirect.js` bouncing a non-member off a members-only page. The
 * sibling caches guard themselves in `./identity.js`'s `forget()`, which is a guard a caller has to
 * remember to run FIRST -- and `initLoyaltyToasts()` does not, because it consults this cache from
 * `whenToastEligible()` long before `startToastTrigger()` reaches the identity guard. Folding the
 * fan into the entry makes an account switch a cache MISS by construction, with no ordering left to
 * get wrong.
 *
 * **The fan is an Auth0 `sub` off the settled AuthState, and it used to be decoded out of the
 * bearer.** Same value, better provenance: the SDK validates the ID token's signature before
 * exposing its claims, where this module used to split a JWT on a dot and read whatever came back.
 * `resources/scripts/auth/state.js` also refuses two values outright -- an empty `sub`, and the
 * literal `'unknown'` that `auth0-login.php` wrote into `auth0_user_id` whenever `/userinfo` was
 * unreadable -- so neither can reach a cache key. That second one is the collision `./identity.js`
 * had to filter for: two different fans could both present it.
 *
 * **The fan is in the VALUE, not in the key**, which is what keeps this to one entry per tab however
 * many fans sign in during it -- and it retires the old flag for free: a tab still carrying the
 * pre-upgrade `'1'` matches nobody, because an Auth0 `sub` always carries a `<connection>|<id>`
 * prefix and so can never be the string `1`.
 *
 * A fan this cannot identify is never a hit and is never recorded. The cost of that is one enrolment
 * read per page, which is the cost of not having the cache at all; the cost of guessing is the bug
 * above.
 *
 * **Nothing is imported here**, which is what lets enrolled.test.js drive it under `node --test`
 * with a hand-built `globalThis.window`. It took a `token` and an import of `claims()` until
 * 2026-09-07.
 */

/** Where the verdict lives. Named in the Debugging section of docs/subsystems/loyalty-toasts.md. */
export const ENROLLED_KEY = 'urc.loyalty.toastEnrolled';

/**
 * Whether this tab has already established that THIS fan is enrolled.
 *
 * @param {string} fan The fan's Auth0 `sub`, or '' when there is none.
 * @return {boolean} Whether the stored verdict belongs to this fan.
 */
export function knownEnrolled( fan ) {
	if ( typeof fan !== 'string' || fan === '' ) {
		return false;
	}

	try {
		return window.sessionStorage.getItem( ENROLLED_KEY ) === fan;
	} catch ( error ) {
		// Private mode, or storage disabled. Costs one extra read per page, nothing else.
		return false;
	}
}

/**
 * Record that THIS fan is enrolled.
 *
 * @param {string} fan The fan's Auth0 `sub`, or '' when there is none.
 * @return {void}
 */
export function rememberEnrolled( fan ) {
	if ( typeof fan !== 'string' || fan === '' ) {
		return;
	}

	try {
		window.sessionStorage.setItem( ENROLLED_KEY, fan );
	} catch ( error ) {
		// Ignored on purpose: the cache is an optimisation, never a gate.
	}
}

/**
 * Drop the stored verdict, because this fan has just left the programme.
 *
 * **Guarded on the fan, and that is not symmetry for its own sake.** The entry holds ONE fan at a
 * time, so an unguarded clear would let one fan's withdrawal throw away a verdict established for
 * somebody else -- costing them a needless enrolment read on the next page for no reason at all.
 * Clearing only when the entry is this fan's makes the call safe to make from anywhere.
 *
 * Called from ../leaveLoyalty.js on a successful withdrawal. It is deliberately NOT part of
 * `resetToastGate()`: that resets the in-memory verdict on an account switch, where the incoming fan
 * makes this a cache miss by construction and there is nothing to clear.
 *
 * @param {string} fan The fan's Auth0 `sub`, or '' when there is none.
 * @return {void}
 */
export function forgetEnrolled( fan ) {
	if ( typeof fan !== 'string' || fan === '' ) {
		return;
	}

	try {
		if ( window.sessionStorage.getItem( ENROLLED_KEY ) === fan ) {
			window.sessionStorage.removeItem( ENROLLED_KEY );
		}
	} catch ( error ) {
		// Same posture as the write: storage that cannot be reached costs a read per page, and
		// there is nothing useful to do about it here.
	}
}
